import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { EyeOff, ShieldCheck, Users, Clock, Gem, ArrowRight, Phone, Plus } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { PortalBanner } from "./PortalBanner";

const HERO = "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const ADVANTAGES = [
  { icon: EyeOff, title: "Confidentialité totale", text: "Le bien n'apparaît sur aucun portail public. La vente reste discrète, à l'abri des regards et du voisinage." },
  { icon: Users, title: "Acquéreurs qualifiés", text: "Le bien est présenté à un cercle restreint d'acquéreurs sérieux et pré-identifiés, avant toute diffusion." },
  { icon: Clock, title: "Avant tout le monde", text: "Nos acquéreurs accèdent aux opportunités en avant-première, souvent avant leur mise sur le marché." },
  { icon: Gem, title: "Biens rares & de prestige", text: "L'off-market concentre les biens d'exception, atypiques ou patrimoniaux, rarement diffusés publiquement." },
  { icon: ShieldCheck, title: "Négociation sereine", text: "Moins de concurrence frontale et de visites inutiles : des échanges directs, qualitatifs et sécurisés." },
];

const STEPS = [
  { n: "01", title: "Détection du bien", text: "Un propriétaire souhaite vendre en toute discrétion, ou un apporteur d'affaires détecte une opportunité." },
  { n: "02", title: "Estimation & valorisation", text: "Estimation juste, reportage premium et mise en valeur (home staging IA) sans diffusion publique." },
  { n: "03", title: "Présentation confidentielle", text: "Le bien est proposé à un réseau ciblé d'acquéreurs qualifiés et de conseillers partenaires." },
  { n: "04", title: "Mise en relation & offre", text: "Les acquéreurs intéressés sont mis en relation, visitent, puis déposent une offre officielle en ligne." },
  { n: "05", title: "Signature", text: "Accompagnement jusqu'à la signature, avec avant-contrats et signature électronique certifiée." },
];

const FAQ = [
  { q: "Qu'est-ce que l'immobilier off-market ?", a: "L'immobilier off-market (ou « hors marché ») désigne des biens à vendre qui ne sont pas diffusés publiquement sur les portails d'annonces classiques. Ils sont proposés en toute confidentialité à un cercle restreint d'acquéreurs qualifiés, souvent avant toute mise sur le marché." },
  { q: "Pourquoi vendre son bien en off-market ?", a: "Pour préserver la confidentialité (aucune annonce publique, pas de « bien qui traîne »), cibler des acquéreurs sérieux, éviter les visites inutiles et négocier plus sereinement. C'est particulièrement adapté aux biens de prestige, atypiques ou patrimoniaux." },
  { q: "Comment acheter un bien off-market ?", a: "En rejoignant le cercle d'acquéreurs qualifiés d'OFF MARKETTE : décrivez votre projet (par écrit ou à la voix via l'Espace Acquéreur) et vous serez informé en priorité des biens confidentiels correspondant à vos critères, avant leur éventuelle diffusion publique." },
  { q: "Qu'est-ce que l'immobilier select ?", a: "L'immobilier select désigne une sélection resserrée de biens de qualité — prestige, emplacement rare ou fort potentiel — choisis pour leur valeur et présentés à une clientèle exigeante. OFF MARKETTE conjugue off-market et immobilier select pour proposer les plus belles opportunités." },
  { q: "L'off-market est-il légal et sécurisé ?", a: "Oui. Il s'agit d'un mode de commercialisation confidentiel parfaitement légal, encadré par des mandats et des conseillers professionnels. Les transactions suivent le même cadre juridique qu'une vente classique (offre officielle, avant-contrats, notaire)." },
  { q: "OFF MARKETTE couvre quelle zone ?", a: "OFF MARKETTE opère en France via son réseau de conseillers, avec un ancrage historique dans l'Oise (60) et la région Hauts-de-France, et un service disponible en 6 langues pour une clientèle internationale." },
];

export default function OffMarketDefinition() {
  const [openStep, setOpenStep] = useState("01");
  const [openFaq, setOpenFaq] = useState(0);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const defLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "name": "Immobilier off-market",
    "description": "Biens immobiliers à vendre non diffusés publiquement, proposés de façon confidentielle à un cercle restreint d'acquéreurs qualifiés avant toute mise sur le marché.",
    "inDefinedTermSet": { "@type": "DefinedTermSet", "name": "Glossaire immobilier OFF MARKETTE" },
  };

  return (
    <PlatformLayout>
      <Helmet>
        <title>L'immobilier off-market : définition, avantages & FAQ | OFF MARKETTE</title>
        <meta name="description" content="Qu'est-ce que l'immobilier off-market (hors marché) ? Définition, avantages, fonctionnement et FAQ. OFF MARKETTE, spécialiste de l'immobilier off-market et select en France." />
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(defLd)}</script>
      </Helmet>

      <PortalBanner
        image={HERO}
        testid="offmarket-hero"
        subtitle="Le guide de l'immobilier confidentiel"
        title="L'immobilier off-market"
      />

      {/* Définition */}
      <section className="py-20 sm:py-28 bg-white" data-testid="offmarket-definition">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 space-y-6">
          <Reveal>
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Définition</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 text-zinc-900">Qu'est-ce que l'off-market ?</h2>
          </Reveal>
          <Reveal>
            <p className="text-base sm:text-lg font-light text-zinc-700 leading-relaxed">
              L'<strong>immobilier off-market</strong> (ou « hors marché ») désigne des biens à vendre qui ne sont
              <strong> pas diffusés publiquement</strong> sur les portails d'annonces classiques. Ces biens sont
              proposés en toute <strong>confidentialité</strong> à un cercle restreint d'acquéreurs qualifiés,
              souvent <strong>avant toute mise sur le marché</strong>.
            </p>
          </Reveal>
          <Reveal>
            <p className="text-base font-light text-zinc-600 leading-relaxed">
              Chez <strong>OFF MARKETTE</strong>, nous avons fait de l'off-market et de l'<strong>immobilier select</strong>
              {" "}notre spécialité : révéler les plus belles opportunités à une clientèle exigeante, en préservant la
              discrétion des vendeurs et l'accès prioritaire des acquéreurs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 bg-[#0B0B0D] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-lg text-[#C19A6B]">Pourquoi l'off-market</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight mt-2" text="Les avantages de la vente confidentielle." />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="offmarket-advantages">
            {ADVANTAGES.map((a, i) => (
              <motion.div key={a.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }} data-testid={`offmarket-adv-${i}`} className="bg-[#111114] border border-white/10 rounded-[5px] p-7 hover:border-[#C19A6B]/40 transition-colors">
                <a.icon className="w-7 h-7 text-[#C19A6B] mb-4" />
                <h3 className="text-lg font-light text-white mb-2">{a.title}</h3>
                <p className="text-sm font-light text-zinc-400 leading-relaxed">{a.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fonctionnement */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Comment ça marche</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 text-zinc-900">De la détection à la signature.</h2>
          </Reveal>
          <div className="space-y-3" data-testid="offmarket-steps">
            {STEPS.map((s, i) => {
              const open = openStep === s.n;
              return (
                <motion.button key={s.n} type="button" data-testid={`offmarket-step-${s.n}`} onClick={() => setOpenStep(open ? null : s.n)}
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: i * 0.08 }}
                  className={`w-full text-left grid grid-cols-12 gap-4 items-center p-6 border rounded-[5px] transition-all duration-300 ${open ? "bg-[#C19A6B]/10 border-[#C19A6B]/50" : "bg-zinc-50 border-zinc-200 hover:border-[#C19A6B]/30"}`}>
                  <div className={`col-span-2 sm:col-span-1 text-3xl font-light ${open ? "text-[#C19A6B]" : "text-zinc-300"}`}>{s.n}</div>
                  <div className="col-span-8 sm:col-span-9">
                    <div className="text-lg font-light text-zinc-900">{s.title}</div>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.p initial={{ height: 0, opacity: 0, marginTop: 0 }} animate={{ height: "auto", opacity: 1, marginTop: 8 }} exit={{ height: 0, opacity: 0, marginTop: 0 }} transition={{ duration: 0.3 }} className="text-sm font-light text-zinc-600 leading-relaxed overflow-hidden">{s.text}</motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="col-span-2 flex justify-end"><Plus className={`w-5 h-5 text-[#C19A6B] transition-transform duration-300 ${open ? "rotate-45" : ""}`} /></div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#0B0B0D] border-y border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-lg text-[#C19A6B]">Questions fréquentes</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight mt-2" text="Tout savoir sur l'off-market." />
          </Reveal>
          <div className="space-y-3" data-testid="offmarket-faq">
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className={`border rounded-[5px] transition-all ${open ? "border-[#C19A6B]/50 bg-[#C19A6B]/[0.06]" : "border-white/10 bg-[#111114]"}`}>
                  <button type="button" data-testid={`offmarket-faq-${i}`} onClick={() => setOpenFaq(open ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
                    <span className="text-base font-light text-white">{f.q}</span>
                    <Plus className={`w-5 h-5 shrink-0 text-[#C19A6B] transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <p className="px-5 pb-5 text-sm font-light text-zinc-400 leading-relaxed">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white" data-testid="offmarket-cta">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight text-zinc-900" text="Accédez aux biens off-market." />
          <p className="text-zinc-600 font-light">Découvrez nos opportunités confidentielles ou confiez-nous la vente de votre bien, en toute discrétion.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/biens-off-market" data-testid="offmarket-cta-listings" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors">Voir les biens off-market <ArrowRight className="w-4 h-4" /></Link>
            <a href="tel:+33688675357" data-testid="offmarket-cta-call" className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors"><Phone className="w-4 h-4" /> Nous contacter</a>
          </div>
        </div>
      </section>
    </PlatformLayout>
  );
}
